import axios from "axios";
import * as Yup from "yup";
import { EMAIL_REGEX } from "@/func/emailValidation";
import { getLeadSendDelay, isTokenStale } from "@/func/leadToken";

class EmailMarketingLeadsProvider {
  constructor() {
    this.baseUrl = "/wp-json/api/v1/email-marketing";
    this.sessionId = sessionStorage.getItem("sessionId") || null;
    this.leadToken = sessionStorage.getItem("leadToken") || null;
    this.tokenReceivedAt = Number(sessionStorage.getItem("leadTokenReceivedAt")) || null;
    this.minDelayMs = Number(sessionStorage.getItem("leadMinDelayMs")) || 0;

    this.validationSchema = Yup.object().shape({
      email: Yup.string().email().matches(EMAIL_REGEX).required(),
      first_name: Yup.string().min(1).required(),
      product_name: Yup.string().required(),
      product_qty: Yup.number().min(1).required(),
    });
  }

  validate(data) {
    try {
      this.validationSchema.validateSync(data, { abortEarly: false });
      return true;
    } catch {
      return false;
    }
  }

  // Sessions started before the lead token existed have no token, so they
  // fetch a new session too.
  async initialize() {
    if (this.sessionId && !isTokenStale(this.leadToken, this.tokenReceivedAt)) {
      return this.sessionId;
    }

    try {
      const { data } = await axios.get(`${this.baseUrl}/session`);
      this.sessionId = data.sessionId;
      this.leadToken = data.leadToken || null;
      this.tokenReceivedAt = Date.now();
      this.minDelayMs = Number(data.minDelayMs) || 0;
      sessionStorage.setItem("sessionId", this.sessionId);
      sessionStorage.setItem("leadToken", this.leadToken || "");
      sessionStorage.setItem("leadTokenReceivedAt", String(this.tokenReceivedAt));
      sessionStorage.setItem("leadMinDelayMs", String(this.minDelayMs));
      return this.sessionId;
    } catch (error) {
      throw new Error("Failed to process your request. Please check your information and try again.");
    }
  }

  async createLead(leadData) {
    // Replaces a token that went stale while the page was open.
    await this.initialize();

    if (!this.sessionId) {
      throw new Error("Failed to process your request. Please refresh the page and try again.");
    }

    const delay = getLeadSendDelay(this.minDelayMs, this.tokenReceivedAt);

    if (delay > 0) {
      await new Promise((resolve) => setTimeout(resolve, delay));
    }

    try {
      const { data } = await axios.post(
        `${this.baseUrl}/createLead`,
        { ...leadData, lead_token: this.leadToken || "" },
        {
          headers: {
            "X-Session-Id": this.sessionId,
          },
        }
      );
      return data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      throw new Error(message);
    }
  }
}

export default EmailMarketingLeadsProvider;
